import { useCallback, useEffect, useRef, useState } from "react";
import type { LabelAccessUpdate, LabelRequestOption, LabelUserCreate, PersonnelAccessEntry, PersonnelAccessHistory, PersonnelAccessSource, PinResult } from "../../../shared/personnel-access/contracts";
import { personnelAccessApi } from "./personnel-access-api";

export function usePersonnelAccessWorkspace() {
  const [items, setItems] = useState<PersonnelAccessEntry[]>([]);
  const [labelRequestOptions, setLabelRequestOptions] = useState<LabelRequestOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<PersonnelAccessSource | "ALL">("ALL");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<PinResult | null>(null);
  const [history, setHistory] = useState<{ key: string; items: PersonnelAccessHistory[] } | null>(null);
  const latestRequest = useRef(0);
  const busyRef = useRef(false);
  const mounted = useRef(false);
  const secretEpoch = useRef(0);
  const hideSecret = useCallback(() => { secretEpoch.current += 1; setRevealed(null); }, []);

  useEffect(() => {
    mounted.current = true;
    const closeSecret = () => hideSecret();
    const visibility = () => { if (document.visibilityState !== "visible") closeSecret(); };
    window.addEventListener("blur", closeSecret);
    document.addEventListener("visibilitychange", visibility);
    return () => { mounted.current = false; latestRequest.current += 1; secretEpoch.current += 1; window.removeEventListener("blur", closeSecret); document.removeEventListener("visibilitychange", visibility); };
  }, [hideSecret]);

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true); setError("");
    try {
      const response = await personnelAccessApi.list();
      if (request === latestRequest.current) { setItems(response.items); setLabelRequestOptions(response.labelRequestOptions); }
    } catch (cause) {
      if (request === latestRequest.current) setError(cause instanceof Error ? cause.message : "Personel ve erişim listesi yüklenemedi.");
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!revealed) return;
    const timeout = window.setTimeout(hideSecret, Math.max(0, Date.parse(revealed.expiresAt) - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [hideSecret, revealed]);

  async function pinCommand(item: PersonnelAccessEntry, command: "reveal" | "generate") {
    const key = `${command}:${item.source}:${item.sourcePersonId}`;
    if (busyRef.current) return;
    busyRef.current = true;
    const secretRequest = secretEpoch.current;
    setBusyKey(key); setError(""); setRevealed(null);
    try {
      const result = command === "reveal"
        ? await personnelAccessApi.revealPin(item.source, item.sourcePersonId)
        : await personnelAccessApi.generatePin(item.source, item.sourcePersonId, item.pinChangedAt);
      if (!mounted.current || secretRequest !== secretEpoch.current || document.visibilityState !== "visible") return;
      setRevealed(result);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "PIN işlemi tamamlanamadı.");
    } finally { busyRef.current = false; if (mounted.current) setBusyKey(null); }
  }

  async function saveLabelAccess(personId: string, input: LabelAccessUpdate | null): Promise<boolean> {
    if (busyRef.current) return false;
    busyRef.current = true;
    const key = `label:${personId}`;
    setBusyKey(key); setError("");
    try {
      if (input) await personnelAccessApi.replaceLabelAccess(personId, input);
      else await personnelAccessApi.removeLabelAccess(personId);
      await load();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Etiket erişimi güncellenemedi.");
      return false;
    } finally { busyRef.current = false; if (mounted.current) setBusyKey(null); }
  }

  async function createLabelUser(input: LabelUserCreate): Promise<boolean> {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusyKey("label:create"); setError("");
    try {
      await personnelAccessApi.createLabelUser(input);
      await load();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Etiket kullanıcısı eklenemedi.");
      return false;
    } finally { busyRef.current = false; if (mounted.current) setBusyKey(null); }
  }

  async function loadHistory(source: PersonnelAccessSource, personId: string) {
    if (busyRef.current) return;
    busyRef.current = true; setBusyKey(`history:${personId}`); setError("");
    try {
      const response = await personnelAccessApi.history(source, personId);
      if (mounted.current) setHistory({ key: `${source}:${personId}`, items: response.items });
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "İşlem geçmişi yüklenemedi.");
    } finally { busyRef.current = false; if (mounted.current) setBusyKey(null); }
  }

  const needle = query.trim().toLocaleLowerCase("tr-TR");
  const visibleItems = items.filter((item) =>
    (source === "ALL" || item.source === source)
    && (!needle || `${item.name} ${item.subtitle ?? ""}`.toLocaleLowerCase("tr-TR").includes(needle)),
  );
  const changeQuery = (value: string) => { hideSecret(); setQuery(value); };
  const changeSource = (value: PersonnelAccessSource | "ALL") => { hideSecret(); setSource(value); };
  return { items: visibleItems, labelRequestOptions, loading, error, query, setQuery: changeQuery, source, setSource: changeSource, busyKey, revealed, hideSecret, history, setHistory, load, pinCommand, saveLabelAccess, createLabelUser, loadHistory };
}
